/**
 * The guest's BroxSpecs draft, remembered across registration.
 *
 * Holds the server-minted session secret and the conversation id, so that after
 * a visitor registers, SpecsDraftModal can offer to resume the exact
 * conversation they were having, and SpecsSurface can claim it.
 *
 * localStorage, not a redirect parameter, DELIBERATELY: the claim then survives
 * landing on /dashboard first, closing the tab, and coming back an hour later -
 * and it needs no change to AuthPage at all. Same browser only, by construction.
 *
 * EVERY read is defensive. This runs on every customer page load (the modal is
 * mounted in CustomerShell), the value is user-writable, and the app has NO
 * ERROR BOUNDARY - a single throw here renders a blank page with no message.
 * So a corrupt, partial, or wrongly-typed value reads as "no draft", and a
 * storage that throws is swallowed.
 */
export const SPECS_DRAFT_KEY = 'broxel-specs-draft'

/**
 * SessionStorage, not localStorage: "Not now" answers THIS visit, not forever.
 * Owned here rather than inside useSpecsDraft so that logout - which is not a
 * hook and must not import one - can reset both halves of this browser's draft
 * state from one module.
 */
export const SPECS_DRAFT_DISMISSED_KEY = 'broxel-specs-draft-dismissed'

export interface SpecsDraft {
  session_id: string
  /**
   * PROVENANCE ONLY - no consumer resolves by it. Both `GET
   * /api/broxspecs/draft` and `POST /api/broxspecs/claim` look the draft up
   * by `session_id` alone, which is the actual credential. `null` means "we
   * don't know it yet" (e.g. the first-turn-failure path, where the error
   * body carries a session_id but no conversation_id) and must NOT be
   * treated as a reason to distrust or drop the rest of the draft.
   */
  conversation_id: number | null
  saved_at: string
}

export function readDraft(): SpecsDraft | null {
  try {
    const raw = localStorage.getItem(SPECS_DRAFT_KEY)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null

    const { session_id, conversation_id, saved_at } = parsed as Record<string, unknown>
    // session_id is the credential and is strictly required: empty,
    // whitespace-only, missing or non-string all reject the whole draft.
    if (typeof session_id !== 'string' || session_id.trim() === '') return null

    // conversation_id is provenance only (see the interface comment above),
    // so a bad value normalises to null rather than discarding a draft whose
    // session_id - the part that matters - is perfectly good.
    const normalizedConversationId = typeof conversation_id === 'number' && Number.isInteger(conversation_id)
      ? conversation_id
      : null

    return {
      session_id,
      conversation_id: normalizedConversationId,
      saved_at: typeof saved_at === 'string' ? saved_at : ''
    }
  } catch {
    return null
  }
}

export function saveDraft(
  draft: Omit<SpecsDraft, 'saved_at' | 'conversation_id'> & { conversation_id?: number | null, saved_at?: string }
): void {
  try {
    localStorage.setItem(SPECS_DRAFT_KEY, JSON.stringify({
      session_id: draft.session_id,
      conversation_id: draft.conversation_id ?? null,
      saved_at: draft.saved_at ?? new Date().toISOString()
    }))
  } catch {
    // Private mode, or quota exceeded. The visitor loses continuity across
    // registration - they do not lose the page they are on.
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(SPECS_DRAFT_KEY)
  } catch {
    // As above.
  }
}

/**
 * Forgets everything this browser remembers about a guest draft.
 *
 * Called on LOGOUT, and that is the point: the draft is bound to the BROWSER,
 * not to a person - the session id is a bearer secret and `POST /broxspecs/claim`
 * moves `user_id` permanently. On a shared machine (an office PC, the event
 * kiosk) the next person to sign in would otherwise be offered, and could
 * silently take ownership of, a stranger's conversation. Logging out is the one
 * moment we can be sure the person who built it has finished with it.
 *
 * The cost is accepted and small: a customer who logs out on their own laptop
 * before claiming loses the resume prompt. The draft itself survives server-side
 * for 30 days, and an unclaimed conversation is only ever reachable by a secret
 * that has now been discarded - so this narrows exposure without deleting work
 * anyone else could still reach.
 */
export function forgetDraft(): void {
  clearDraft()
  try {
    sessionStorage.removeItem(SPECS_DRAFT_DISMISSED_KEY)
  } catch {
    // As above.
  }
}
